/**
 * API Performance Monitor
 * 
 * This utility monitors API performance, tracks metrics, and provides
 * insights for optimization and debugging.
 */

import { PERFORMANCE_CONFIG } from '@configs/api.config';

/**
 * Performance metrics storage
 */
class PerformanceMetrics {
  constructor() {
    this.metrics = {
      requests: new Map(),
      errors: new Map(),
      cacheHits: 0,
      cacheMisses: 0,
      totalRequests: 0,
      totalErrors: 0
    };
    this.startTime = Date.now();
  }

  /**
   * Record a request
   */
  recordRequest(url, method, duration, success = true) {
    if (!PERFORMANCE_CONFIG.ENABLED) return;

    const key = `${method.toUpperCase()} ${url}`;
    
    if (!this.metrics.requests.has(key)) {
      this.metrics.requests.set(key, {
        count: 0,
        totalDuration: 0,
        avgDuration: 0,
        minDuration: Infinity,
        maxDuration: 0,
        errors: 0,
        successRate: 100
      });
    }

    const metric = this.metrics.requests.get(key);
    metric.count++;
    metric.totalDuration += duration;
    metric.avgDuration = metric.totalDuration / metric.count;
    metric.minDuration = Math.min(metric.minDuration, duration);
    metric.maxDuration = Math.max(metric.maxDuration, duration);

    if (!success) {
      metric.errors++;
    }
    
    metric.successRate = ((metric.count - metric.errors) / metric.count) * 100;

    this.metrics.totalRequests++;
    if (!success) {
      this.metrics.totalErrors++;
    }

    // Log slow requests
    if (duration > PERFORMANCE_CONFIG.THRESHOLDS.SLOW_REQUEST) {
      console.warn(`🐌 Slow API request: ${key} took ${duration}ms`);
    }

    if (duration > PERFORMANCE_CONFIG.THRESHOLDS.VERY_SLOW_REQUEST) {
      console.error(`🚨 Very slow API request: ${key} took ${duration}ms`);
    }
  }

  /**
   * Record an error
   */
  recordError(url, method, error) {
    if (!PERFORMANCE_CONFIG.ENABLED) return;

    const key = `${method.toUpperCase()} ${url}`;
    
    if (!this.metrics.errors.has(key)) {
      this.metrics.errors.set(key, {
        count: 0,
        types: new Map()
      });
    }

    const errorMetric = this.metrics.errors.get(key);
    errorMetric.count++;

    const errorType = error.response?.status || error.code || 'UNKNOWN';
    const typeCount = errorMetric.types.get(errorType) || 0;
    errorMetric.types.set(errorType, typeCount + 1);
  }

  /**
   * Record cache hit/miss
   */
  recordCacheHit(hit = true) {
    if (!PERFORMANCE_CONFIG.ENABLED) return;

    if (hit) {
      this.metrics.cacheHits++;
    } else {
      this.metrics.cacheMisses++;
    }
  }

  /**
   * Get performance summary
   */
  getSummary() {
    const totalCacheRequests = this.metrics.cacheHits + this.metrics.cacheMisses;
    const cacheHitRate = totalCacheRequests > 0 
      ? (this.metrics.cacheHits / totalCacheRequests) * 100 
      : 0;

    const overallErrorRate = this.metrics.totalRequests > 0
      ? (this.metrics.totalErrors / this.metrics.totalRequests) * 100
      : 0;

    const uptime = Date.now() - this.startTime;

    return {
      uptime,
      totalRequests: this.metrics.totalRequests,
      totalErrors: this.metrics.totalErrors,
      overallErrorRate,
      cacheHitRate,
      requestsPerMinute: (this.metrics.totalRequests / (uptime / 60000)).toFixed(2),
      topSlowEndpoints: this.getTopSlowEndpoints(),
      topErrorEndpoints: this.getTopErrorEndpoints()
    };
  }

  /**
   * Get top slow endpoints
   */
  getTopSlowEndpoints(limit = 5) {
    return Array.from(this.metrics.requests.entries())
      .sort(([, a], [, b]) => b.avgDuration - a.avgDuration)
      .slice(0, limit)
      .map(([endpoint, metrics]) => ({
        endpoint,
        avgDuration: Math.round(metrics.avgDuration),
        maxDuration: metrics.maxDuration,
        count: metrics.count
      }));
  }

  /**
   * Get top error endpoints
   */
  getTopErrorEndpoints(limit = 5) {
    return Array.from(this.metrics.errors.entries())
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, limit)
      .map(([endpoint, metrics]) => ({
        endpoint,
        errorCount: metrics.count,
        errorTypes: Object.fromEntries(metrics.types)
      }));
  }

  /**
   * Reset metrics
   */
  reset() {
    this.metrics = {
      requests: new Map(),
      errors: new Map(),
      cacheHits: 0,
      cacheMisses: 0,
      totalRequests: 0,
      totalErrors: 0
    };
    this.startTime = Date.now();
  }

  /**
   * Export metrics for external analysis
   */
  exportMetrics() {
    return {
      summary: this.getSummary(),
      requests: Object.fromEntries(this.metrics.requests),
      errors: Object.fromEntries(this.metrics.errors),
      timestamp: new Date().toISOString()
    };
  }
}

/**
 * Global performance monitor instance
 */
const performanceMonitor = new PerformanceMetrics();

/**
 * Create axios interceptor for performance monitoring
 */
export const createPerformanceInterceptor = (axiosInstance) => {
  // Request interceptor to track start time
  axiosInstance.interceptors.request.use(
    (config) => {
      config.metadata = { startTime: Date.now() };
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to track completion time
  axiosInstance.interceptors.response.use(
    (response) => {
      const duration = Date.now() - response.config.metadata.startTime;
      performanceMonitor.recordRequest(
        response.config.url,
        response.config.method,
        duration,
        true
      );
      return response;
    },
    (error) => {
      const duration = error.config?.metadata?.startTime 
        ? Date.now() - error.config.metadata.startTime 
        : 0;
      
      if (error.config) {
        performanceMonitor.recordRequest(
          error.config.url,
          error.config.method,
          duration,
          false
        );
        performanceMonitor.recordError(
          error.config.url,
          error.config.method,
          error
        );
      }
      
      return Promise.reject(error);
    }
  );

  return performanceMonitor;
};

/**
 * React hook for performance monitoring
 */
export const useApiPerformance = () => {
  return {
    getSummary: () => performanceMonitor.getSummary(),
    getMetrics: () => performanceMonitor.exportMetrics(),
    reset: () => performanceMonitor.reset(),
    recordCacheHit: (hit) => performanceMonitor.recordCacheHit(hit)
  };
};

/**
 * Performance dashboard component data
 */
export const getPerformanceDashboardData = () => {
  const summary = performanceMonitor.getSummary();
  
  return {
    overview: {
      totalRequests: summary.totalRequests,
      errorRate: `${summary.overallErrorRate.toFixed(2)}%`,
      cacheHitRate: `${summary.cacheHitRate.toFixed(2)}%`,
      requestsPerMinute: summary.requestsPerMinute,
      uptime: formatUptime(summary.uptime)
    },
    slowEndpoints: summary.topSlowEndpoints,
    errorEndpoints: summary.topErrorEndpoints,
    alerts: generateAlerts(summary)
  };
};

/**
 * Format uptime in human readable format
 */
const formatUptime = (milliseconds) => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

/**
 * Generate performance alerts
 */
const generateAlerts = (summary) => {
  const alerts = [];

  if (summary.overallErrorRate > PERFORMANCE_CONFIG.THRESHOLDS.HIGH_ERROR_RATE * 100) {
    alerts.push({
      type: 'error',
      message: `High error rate: ${summary.overallErrorRate.toFixed(2)}%`
    });
  }

  if (summary.cacheHitRate < 50) {
    alerts.push({
      type: 'warning',
      message: `Low cache hit rate: ${summary.cacheHitRate.toFixed(2)}%`
    });
  }

  summary.topSlowEndpoints.forEach(endpoint => {
    if (endpoint.avgDuration > PERFORMANCE_CONFIG.THRESHOLDS.SLOW_REQUEST) {
      alerts.push({
        type: 'warning',
        message: `Slow endpoint: ${endpoint.endpoint} (${endpoint.avgDuration}ms avg)`
      });
    }
  });

  return alerts;
};

export default {
  performanceMonitor,
  createPerformanceInterceptor,
  useApiPerformance,
  getPerformanceDashboardData
};
